import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { frameTimecode } from '../../domain/frame-timecode';
import type { MarkerTime } from './timeline-scale';

export function TimelineCluster({ button, items, label = 'Keyframe', onSelect, onClose, onReveal }: {
  label?: string; button: HTMLButtonElement; items: MarkerTime[]; onSelect: (id: string) => void; onClose: () => void;
  onReveal?: ((items: MarkerTime[]) => void) | undefined;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 8, top: 8 });
  useLayoutEffect(() => {
    const place = () => {
      const anchor = button.getBoundingClientRect(), panel = host.current!.getBoundingClientRect();
      setPosition({ left: Math.max(8, Math.min(innerWidth - panel.width - 8, anchor.left - 100)),
        top: Math.max(8, Math.min(innerHeight - panel.height - 8, anchor.top - panel.height - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [button, items.length, onReveal]);
  useEffect(() => {
    host.current?.querySelector<HTMLButtonElement>('[data-cluster-key]')?.focus();
    const outside = (event: Event) => { if (!host.current?.contains(event.target as Node) && !button.contains(event.target as Node)) onClose(); };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [button]);
  return createPortal(<div ref={host} className="timeline-cluster" role="group" aria-label="Nearby keyframes"
    data-theme={button.closest('[data-theme]')?.getAttribute('data-theme')}
    style={position}
    onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); onClose(); button.focus(); }
    }}>
    <div className="timeline-cluster-heading"><strong>{label} · {items.length} keys</strong>
      <button type="button" aria-label="Close nearby keyframes" onClick={() => { onClose(); button.focus(); }}>×</button></div>
    {items.map(item => <button type="button" key={item.id} data-cluster-key onClick={() => { onSelect(item.id); onClose(); }}>{label} · {frameTimecode(item.frame)}</button>)}
    {onReveal && <button type="button" className="timeline-cluster-reveal" onClick={() => { onReveal(items); onClose(); }}>Zoom to keys</button>}
  </div>, button.closest('.workbench') ?? document.body);
}
